import styles from "./client-profile-access-settings-content.module.css";

import { TimePicker } from "../../../../components/time-picker";
import {
  ClientProfileAccessSettings,
  AccessLocations,
  AccessAreaType,
} from "../../store/types";
import { observer } from "mobx-react";
import { ChangeEvent } from "react";
import { SelectInput, SwitchInput } from "../../../../components/form-inputs";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

interface ClientProfileSettingsContentProps {
  clientAccess: ClientProfileAccessSettings;
}

function ClientProfileAccessSettingsContent(
  props: ClientProfileSettingsContentProps
) {
  const { t } = useTranslation("client-profile");
  const { clientAccess } = props;

  const { state } = useLocation();

  const render = () => {
    return (
      <div>
        <ul className={styles["flex-container"]}>
          {renderTimeIntervalTitle()}
          {renderTimeIntervalSettings()}

          {renderAccessLocationsTitle()}
          {renderAccessLocationSettings()}
        </ul>
      </div>
    );
  };

  const renderTimeIntervalTitle = () => {
    const { type } = state;
    let prefix = "Day";

    if (type === AccessAreaType.NIGHTIME) {
      prefix = "Night";
    } else if (type === AccessAreaType.NONE) {
      prefix = "Custom";
    }

    if (type === AccessAreaType.NONE) return null;

    return (
      <li
        className={`${styles["item"]} ${styles["odd-tab"]} ${styles["settings-odd-tab"]}`}
      >
        <div
          className={`${styles["settings-title"]} ${styles["settings-col-1"]}`}
        >
          {t(prefix + "time interval")}
        </div>
        <div
          className={`${styles["settings-title"]} ${styles["settings-col-2"]}`}
        >
          {t("Start hour")}
        </div>
        <div
          className={`${styles["settings-title"]} ${styles["settings-col-3"]}`}
        >
          {t("End hour")}
        </div>
      </li>
    );
  };

  const renderTimeIntervalSettings = () => {
    const daytimeInterval = clientAccess.getDaytimeInterval();
    const nighttimeInterval = clientAccess.getNighttimeInterval();

    const currentAccessArea = getCurrentEditingInterval();

    const { type } = state;
    let endHour: string | null = nighttimeInterval.accessAreaStartTime;
    let endHourAccessType = t("Night time access start");

    if (type === AccessAreaType.NIGHTIME) {
      endHour = daytimeInterval.accessAreaStartTime;
      endHourAccessType = t("Day time access start");
    } else if (type === AccessAreaType.NONE) {
      endHour = null;
      endHourAccessType = "";
    }

    if (endHour) {
      const formattedEndHour =
        endHour.length > 5 ? endHour.substring(0, 5) : endHour;

      return (
        <li
          className={`${styles["item"]} ${styles["even-tab"]} ${styles["settings-even-tab"]}`}
        >
          <div
            className={`${styles["settings-text"]} ${styles["settings-col-1"]}`}
          >
            {t("Active interval")}
          </div>
          <div className={styles["settings-col-2"]}>
            <TimePicker
              value={currentAccessArea.accessAreaStartTime}
              limitAt={endHour}
              onChange={(value) => {
                currentAccessArea.accessAreaStartTime = value;
              }}
            />
          </div>
          <div
            className={`${styles["settings-text"]} ${styles["settings-col-3"]}`}
          >
            {formattedEndHour + " - " + t(endHourAccessType)}
          </div>
        </li>
      );
    }

    return null;
  };

  const renderAccessLocationsTitle = () => {
    const { type } = state;
    let timeAccess = "Day time access";

    if (type === AccessAreaType.NIGHTIME) {
      timeAccess = "Night time access";
    } else if (type === AccessAreaType.NONE) {
      timeAccess = "Custom time access";
    }

    return (
      <li
        className={`${styles["item"]} ${styles["odd-tab"]} ${styles["settings-odd-tab"]}`}
      >
        <div
          className={`${styles["settings-title"]} ${styles["settings-col-1"]}`}
        >
          {t(timeAccess)}
        </div>
        <div
          className={`${styles["settings-title"]} ${styles["settings-col-2"]}`}
        >
          {t("Active")}
        </div>
        <div
          className={`${styles["settings-title"]} ${styles["settings-col-3"]}`}
        >
          {t("Access locations")}
        </div>
      </li>
    );
  };

  const renderAccessLocationSettings = () => {
    const currentAccessArea = getCurrentEditingInterval();

    const dropdownData = convertToDropdownValues(
      clientAccess.clientProfileAccessLocations
    );

    if (currentAccessArea.accessLocationId === 0) {
      currentAccessArea.accessLocationId = dropdownData[0].id;
    }

    let currentDropdownValue = dropdownData.find(
      (x: any) => x.id === currentAccessArea.accessLocationId
    );

    if (currentDropdownValue === undefined) {
      currentDropdownValue = dropdownData[0];
    }

    return (
      <li
        className={`${styles["item"]} ${styles["even-tab"]} ${styles["settings-even-tab"]}`}
      >
        <div
          className={`${styles["settings-text"]} ${styles["settings-col-1"]}`}
        ></div>
        <div className={styles["settings-col-2"]}>
          <SwitchInput
            checked={currentAccessArea.isAccessGroupToggleActive}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              currentAccessArea.isAccessGroupToggleActive =
                event.target.checked;
            }}
          />
        </div>
        <div className={styles["settings-col-3"]}>
          <SelectInput
            options={dropdownData}
            value={currentDropdownValue}
            onChange={(_event: any, value: any) => {
              const currentAccessArea = getCurrentEditingInterval();
              currentAccessArea.accessLocationId = value.id;
            }}
            data={dropdownData}
          ></SelectInput>
        </div>
      </li>
    );
  };

  const convertToDropdownValues = (object: AccessLocations[]) => {
    if (object.length === 0) {
      return [{ id: 0, value: t("No access available") }];
    }

    const dropdownValue: any = [];
    object.forEach((location) => {
      dropdownValue.push({
        value: location.name,
        id: location.accessId,
      });
    });
    return dropdownValue;
  };

  const getCurrentEditingInterval = () => {
    const daytimeInterval = clientAccess.getDaytimeInterval();
    const nighttimeInterval = clientAccess.getNighttimeInterval();
    const customtimeInterval = clientAccess.getCustomInterval();

    const { type } = state;
    let interval = daytimeInterval;

    if (type === AccessAreaType.NIGHTIME) {
      interval = nighttimeInterval;
    } else if (type === AccessAreaType.NONE) {
      interval = customtimeInterval;
    }
    return interval;
  };

  return render();
}

export default observer(ClientProfileAccessSettingsContent);
